<template>
  <Modal
    v-model:visible="isVisible"
    class="macropoint-additional-info-modal"
    :header="t('shipments.additionalInfoRequiredForTracking')"
    @keypress.esc="isVisible = false"
  >
    <InputWrapper
      v-if="requiresRefNum(WellKnownNaturalId.TRUCK_NUMBER)"
      v-slot="{ ariaProps, inputClasses, inputId, invalid }"
      class="pb-2"
      input-id="macropoint-additional-info-modal-truck-number"
      :label="t('shipments.truckNumber')"
      :errors="v$.truckNumber.$errors"
    >
      <InputText
        :id="inputId"
        v-model="macroPointInfo.truckNumber"
        v-bind="ariaProps"
        :class="inputClasses"
        :invalid="invalid"
        :placeholder="t('shipments.truckNumber')"
      />
    </InputWrapper>

    <InputWrapper
      v-if="requiresRefNum(WellKnownNaturalId.TRAILER_NUMBER)"
      v-slot="{ ariaProps, inputClasses, inputId, invalid }"
      class="pb-2"
      input-id="macropoint-additional-info-modal-trailer-number"
      :label="t('shipments.trailerNumber')"
      :errors="v$.trailerNumber.$errors"
    >
      <InputText
        :id="inputId"
        v-model="macroPointInfo.trailerNumber"
        v-bind="ariaProps"
        :class="inputClasses"
        :invalid="invalid"
        :placeholder="t('shipments.trailerNumber')"
      />
    </InputWrapper>

    <InputWrapper
      v-if="requiresRefNum(WellKnownNaturalId.DRIVER_CELL_NUMBER)"
      v-slot="{ ariaProps, inputClasses, inputId, invalid }"
      input-id="macropoint-additional-info-modal-driver-number"
      :label="t('shipments.driverCellNumber')"
      :errors="v$.driverNumber.$errors"
    >
      <InputText
        :id="inputId"
        v-model="macroPointInfo.driverNumber"
        v-bind="ariaProps"
        :class="inputClasses"
        :invalid="invalid"
        :placeholder="t('shipments.driverCellNumber')"
      />
    </InputWrapper>

    <template #footer>
      <RygenButton
        mode="tertiary"
        :label="t('common.cancel')"
        @click="isVisible = false"
      />

      <RygenButton
        :label="t('common.save')"
        icon="pi pi-check"
        @click="onSubmit"
      />
    </template>
  </Modal>
</template>

<script lang="ts" setup>
import { ValidationRule } from '@vuelidate/core';
import { computed, provide, Ref, ref, toRefs, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { required, useUid, useValidations } from '@rygen/rygen-ui/composables';
import {
  ReferenceNumberTypeLevel,
  ReferenceNumberTypeWellKnownNaturalId,
} from '@rygen/rygen-ui/model';

import { ReferenceNumber, ReferenceNumberSource } from '@/model';

import RygenButton from '@rygen/rygen-ui/components/button/RygenButton.vue';
import InputWrapper from '@rygen/rygen-ui/components/form/InputWrapper.vue';
import Modal from '@rygen/rygen-ui/components/ui/Modal.vue';
import InputText from 'primevue/inputtext';

const WellKnownNaturalId = ReferenceNumberTypeWellKnownNaturalId;

const isVisible = defineModel('visible', {
  type: Boolean,
  default: () => false,
});

const props = defineProps({
  shipmentId: {
    type: Number,
    default: undefined,
  },
  requiredReferenceNumbers: {
    type: Array as () => string[],
    default: () => [],
  },
  existingReferenceNumbers: {
    type: Array as () => ReferenceNumber[],
    default: () => [],
  },
});
const { shipmentId, requiredReferenceNumbers, existingReferenceNumbers } =
  toRefs(props);
const emit = defineEmits<{
  (e: 'update:values', referenceNumbers: ReferenceNumber[]): void;
}>();

const { t } = useI18n();

const requiresRefNum = (naturalId: string) =>
  requiredReferenceNumbers.value.includes(naturalId);

type MacroPointInfo = {
  trailerNumber?: string;
  truckNumber?: string;
  driverNumber?: string;
};

const findExistingValue = (naturalId: string) =>
  existingReferenceNumbers.value.find((r) => r.typeNaturalId === naturalId)
    ?.value;

const macroPointInfo: Ref<MacroPointInfo> = ref({
  trailerNumber: undefined,
  truckNumber: undefined,
  driverNumber: undefined,
});

watch(isVisible, (visible) => {
  if (visible) {
    macroPointInfo.value.truckNumber = findExistingValue(
      WellKnownNaturalId.TRUCK_NUMBER,
    );
    macroPointInfo.value.trailerNumber = findExistingValue(
      WellKnownNaturalId.TRAILER_NUMBER,
    );
    macroPointInfo.value.driverNumber = findExistingValue(
      WellKnownNaturalId.DRIVER_CELL_NUMBER,
    );
  }
});

const uid = useUid('macropoint-additional-info-modal').uid;
provide('scope', uid);

const rules = computed(() => {
  const r: Record<string, Record<string, ValidationRule>> = {
    truckNumber: {},
    trailerNumber: {},
    driverNumber: {},
  };

  if (requiresRefNum(WellKnownNaturalId.TRUCK_NUMBER)) {
    r.truckNumber.required = required(t('shipments.truckNumber'));
  }
  if (requiresRefNum(WellKnownNaturalId.TRAILER_NUMBER)) {
    r.trailerNumber.required = required(t('shipments.trailerNumber'));
  }
  if (requiresRefNum(WellKnownNaturalId.DRIVER_CELL_NUMBER)) {
    r.driverNumber.required = required(t('shipments.driverCellNumber'));
  }
  return r;
});

const { v$, validate } = useValidations(macroPointInfo, rules, uid);

const onSubmit = () => {
  if (!validate()) {
    return;
  }

  const newReferenceNumbers: Array<ReferenceNumber> = [];
  if (macroPointInfo.value.trailerNumber) {
    newReferenceNumbers.push(new ReferenceNumber({
      typeNaturalId: ReferenceNumberTypeWellKnownNaturalId.TRAILER_NUMBER,
      value: macroPointInfo.value.trailerNumber.replace(/[^\0-\x7F]/g, ''),
      source: new ReferenceNumberSource({
        type: ReferenceNumberTypeLevel.SHIPMENT,
        resourceId: shipmentId?.value,
      }),
    }));
  }

  if (macroPointInfo.value.truckNumber) {
    newReferenceNumbers.push(new ReferenceNumber({
      typeNaturalId: ReferenceNumberTypeWellKnownNaturalId.TRUCK_NUMBER,
      value: macroPointInfo.value.truckNumber.replace(/[^\0-\x7F]/g, ''),
      source: new ReferenceNumberSource({
        type: ReferenceNumberTypeLevel.SHIPMENT,
        resourceId: shipmentId?.value,
      }),
    }));
  }

  if (macroPointInfo.value.driverNumber) {
    newReferenceNumbers.push(new ReferenceNumber({
      typeNaturalId: ReferenceNumberTypeWellKnownNaturalId.DRIVER_CELL_NUMBER,
      value: macroPointInfo.value.driverNumber.replace(/[^\0-\x7F]/g, ''),
      source: new ReferenceNumberSource({
        type: ReferenceNumberTypeLevel.SHIPMENT,
        resourceId: shipmentId?.value,
      }),
    }));
  }

  emit('update:values', newReferenceNumbers);
  macroPointInfo.value.truckNumber = undefined;
  macroPointInfo.value.trailerNumber = undefined;
  macroPointInfo.value.driverNumber = undefined;
  isVisible.value = false;
};
</script>
